import { SendHorizontal } from 'lucide-react';
import { useRef, useState } from 'react';

type ChatComposerProps = {
    disabled: boolean;
    placeholder?: string | null;
    onSend: (text: string) => void;
};

const MAX_TEXTAREA_HEIGHT = 120;

function syncHeight(el: HTMLTextAreaElement): void {
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`;
}

export function WidgetChatComposer({
    disabled,
    placeholder,
    onSend,
}: ChatComposerProps) {
    const [value, setValue] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const submit = () => {
        const text = value.trim();

        if (!text || disabled) {
            return;
        }

        onSend(text);
        setValue('');

        const el = textareaRef.current;

        if (el) {
            el.style.height = 'auto';
            requestAnimationFrame(() => el.focus());
        }
    };

    return (
        <div className="op-composer">
            <textarea
                ref={textareaRef}
                className="op-input"
                rows={1}
                value={value}
                disabled={disabled}
                placeholder={placeholder ?? 'Type a message…'}
                aria-label="Type a message"
                title="Enter to send, Shift + Enter for a new line"
                onChange={(e) => {
                    setValue(e.target.value);
                    syncHeight(e.target);
                }}
                onKeyDown={(e) => {
                    if (e.key !== 'Enter' || e.shiftKey) {
                        return;
                    }

                    const coarse =
                        window.matchMedia('(pointer: coarse)').matches;

                    if (!coarse) {
                        e.preventDefault();
                        submit();
                    }
                }}
            />
            <button
                type="button"
                className="op-send"
                aria-label="Send message"
                disabled={disabled || value.trim().length === 0}
                onClick={submit}
            >
                <SendHorizontal aria-hidden="true" />
            </button>
        </div>
    );
}
